import { mat4, vec3, vec4 } from 'gl-matrix';
import { Plane } from './Plane';

// Scratch vectors — allocated once, reused every frame
const _pv = vec3.create();
const _nv = vec3.create();
const _clipOut = vec4.create();

export class ViewFrustum {
  public planes: Plane[];

  // Frustum corner positions (written into by refresh, readable for debug viz)
  readonly nbl = vec3.create();
  readonly fbl = vec3.create();
  readonly ntl = vec3.create();
  readonly ftl = vec3.create();
  readonly nbr = vec3.create();
  readonly fbr = vec3.create();
  readonly ntr = vec3.create();
  readonly ftr = vec3.create();

  /**
   * @param invertedProjection inverse of the camera's `projection × view` matrix
   * @param reversedZ true if `invertedProjection` was built from a reversed-Z
   *   projection (NDC z=+1 maps to near plane). The main scene camera uses
   *   reversed-Z; light cameras, impostor ortho, and the existing test suite
   *   use forward-Z, which is the default.
   */
  constructor(
    private invertedProjection: mat4,
    private reversedZ: boolean = false,
  ) {
    if (!this.invertedProjection) {
      throw new Error(`invertedProjection is null`);
    }

    this.planes = [
      new Plane('left'),
      new Plane('top'),
      new Plane('right'),
      new Plane('bottom'),
      new Plane('near'),
      new Plane('far'),
    ];
    this.refreshCorners();
    this.refreshPlanes();
  }

  public refresh(): void {
    this.refreshCorners();
    this.refreshPlanes();
  }

  public containsBoundingBox(box: {
    min: vec3 | number[];
    max: vec3 | number[];
  }): FrumstumContainment {
    // http://www.lighthouse3d.com/tutorials/view-frustum-culling/geometric-approach-testing-boxes-ii/
    let result = FrumstumContainment.Full;
    const bmin = box.min;
    const bmax = box.max;

    for (let pi = 0; pi < 6; pi++) {
      const plane = this.planes[pi];
      const n = plane.normal;

      // Inline positive vertex: select max component when normal >= 0, else min
      _pv[0] = n[0] >= 0 ? bmax[0] : bmin[0];
      _pv[1] = n[1] >= 0 ? bmax[1] : bmin[1];
      _pv[2] = n[2] >= 0 ? bmax[2] : bmin[2];

      if (plane.determineDistance(_pv) < 0) {
        return FrumstumContainment.None;
      }

      // Inline negative vertex: opposite selection
      _nv[0] = n[0] >= 0 ? bmin[0] : bmax[0];
      _nv[1] = n[1] >= 0 ? bmin[1] : bmax[1];
      _nv[2] = n[2] >= 0 ? bmin[2] : bmax[2];

      if (plane.determineDistance(_nv) < 0) {
        result = FrumstumContainment.Partial;
      }
    }

    return result;
  }

  public containsBoundingSphere(sphere: {
    position: vec3 | number[];
    radius: number;
  }): FrumstumContainment {
    let result = FrumstumContainment.Full;

    for (let pi = 0; pi < 6; pi++) {
      const plane = this.planes[pi];
      const dist = plane.determineDistance(sphere.position);

      if (dist < -sphere.radius) {
        return FrumstumContainment.None;
      } else if (dist < sphere.radius) {
        result = FrumstumContainment.Partial;
      }
    }

    return result;
  }

  public containsPoint(point: vec3 | number[]): FrumstumContainment {
    for (let pi = 0; pi < 6; pi++) {
      if (this.planes[pi].determineDistance(point) < 0) {
        return FrumstumContainment.None;
      }
    }

    return FrumstumContainment.Full;
  }

  private refreshCorners(): void {
    // Reversed-Z: NDC z = +1 is the near plane, NDC z = -1 the far plane.
    // Forward-Z: the opposite.
    const zNear = this.reversedZ ? 1.0 : -1.0;
    const zFar = this.reversedZ ? -1.0 : 1.0;
    this.unprojectInto(this.nbl, -1, -1, zNear);
    this.unprojectInto(this.fbl, -1, -1, zFar);
    this.unprojectInto(this.ntl, -1, 1, zNear);
    this.unprojectInto(this.ftl, -1, 1, zFar);
    this.unprojectInto(this.nbr, 1, -1, zNear);
    this.unprojectInto(this.fbr, 1, -1, zFar);
    this.unprojectInto(this.ntr, 1, 1, zNear);
    this.unprojectInto(this.ftr, 1, 1, zFar);
  }

  private refreshPlanes(): void {
    this.planes[0].updateFromPoints(this.ntl, this.nbl, this.fbl); // left
    this.planes[1].updateFromPoints(this.ntr, this.ntl, this.ftl); // top
    this.planes[2].updateFromPoints(this.nbr, this.ntr, this.ftr); // right
    this.planes[3].updateFromPoints(this.nbl, this.nbr, this.fbr); // bottom
    this.planes[4].updateFromPoints(this.ntl, this.ntr, this.nbr); // near
    this.planes[5].updateFromPoints(this.ftr, this.ftl, this.fbl); // far
  }

  /** Unproject a clip-space point into the pre-allocated output vec3. */
  private unprojectInto(out: vec3, x: number, y: number, z: number): void {
    vec4.set(_clipOut, x, y, z, 1);
    vec4.transformMat4(_clipOut, _clipOut, this.invertedProjection);
    const w = _clipOut[3];
    out[0] = _clipOut[0] / w;
    out[1] = _clipOut[1] / w;
    out[2] = _clipOut[2] / w;
  }
}

export enum FrumstumContainment {
  None,
  Full,
  Partial,
}
